import memoize from "lodash/memoize";
import * as LocalBrandConfig from "../../../shared/node/BrandConfig";

import * as Graph from "../../GeneratedGraphQL";

import {
  getContrast,
  getContrastWithWhite,
} from "./utils/getContrastWithWhite";
import { applyOpacity } from "./utils/applyOpacity";

// Names of the custom font families.
export type CustomFonts = { bold: string; regular: string };

/**
 * Flat map of CSS custom property names (e.g. "--d-surface") to their
 * resolved values, ready to be injected via BrandStyleTag and consumed by
 * Tailwind utility classes registered in styles/tailwind.css (bg-b-surface,
 * text-b-muted, etc.)
 */
export type BrandCssVars = Record<string, string>;

export type ResolvedThemeName = LocalBrandConfig.ResolvedTheme;

/* eslint-disable @typescript-eslint/member-ordering */
export type Theme = {
  colours: {
    /**
     * Primary custom colour set by the client.
     */
    primary: string;
    /**
     * Colour of text to appear on a background that is the primary colour.
     * Can be either white or dark, based on contrast ratio.
     */
    textOnPrimary: string;
    /**
     * Colour of text intended to be of the primary colour on a white
     * background. Usually the same as primary, but may be changed to dark if
     * the contrast ratio of the primary colour and white is too low.
     */
    primaryText: string;

    /**
     * Secondary brand colour set by the client
     */
    secondary: string;
    /**
     * Colour of text to appear on a background that is the secondary colour.
     * Can be either white or dark, based on contrast ratio.
     */
    textOnSecondary: string;
    /**
     * Colour of text intended to be of the secondary colour on a white
     * background. Usually the same as secondary, but may be changed to dark if
     * the contrast ratio of the secondary colour and white is too low.
     */
    secondaryText: string;

    white: string;
    dark: string;
    black: string;

    error: string;
    success: string;
    info: string;

    /**
     * Boolean values representing contrast ratios of primary and secondary
     * custom colours as foregrounds and backgrounds.
     */
    isDark: {
      /**
       * True if the primary custom colour is considered a dark background (WCAG
       * contrast ratio >= 3)
       */
      primaryBackground: boolean;
      /**
       * True if the primary custom colour is considered a dark foreground (WCAG
       * contrast ratio >= 4.5)
       */
      primaryForeground: boolean;
      /**
       * True if the secondary custom colour is considered a dark background
       * (WCAG contrast ratio >= 3)
       */
      secondaryBackground: boolean;
      /**
       * True if the secondary custom colour is considered a dark foreground
       * (WCAG contrast ratio >= 4.5)
       */
      secondaryForeground: boolean;
    };
  };
  fonts: CustomFonts;
  /**
   * Resolved brand-customisation fields (theme.ts Phase 2/3 additions). All
   * of these fall back to sensible defaults when the corresponding
   * CustomColours field is absent, so existing behaviour is unchanged for
   * booking tools with no brand configuration.
   */
  brand: {
    /** The resolved theme bucket ("light" | "dark") this Theme was generated for. */
    theme: ResolvedThemeName;
    logoUrl?: string;
    fontUrl?: string;
    fontFamily?: string;
    pageBackground?: string;
    /**
     * Flat map of CSS custom property names to values, for injection via
     * BrandStyleTag and consumption by Tailwind utilities (bg-b-surface etc).
     */
    cssVars: BrandCssVars;
  };
};
/* eslint-enable @typescript-eslint/member-ordering */

/**
 * Converts the GraphQL-generated `Brand`/`BrandPalette` wire shape into
 * `@appointedd/zod-types`'s canonical `LocalBrandConfig.BrandConfig`
 * shape, so it can be fed directly into `resolveBrandPalette`/
 * `resolveActiveTheme`. Field names are identical between the two shapes
 * (see typeDefs.ts's `BrandPalette`/`Brand` types) except for
 * fontUrl/fontFamily -> brandFontUrl/brandFontFamily, so this is close to
 * an identity mapping.
 */
const brandThemeModeMap: Record<
  Graph.BrandThemeMode,
  "auto" | "light" | "dark"
> = {
  [Graph.BrandThemeMode.Auto]: "auto",
  [Graph.BrandThemeMode.Light]: "light",
  [Graph.BrandThemeMode.Dark]: "dark",
};

/**
 * Converts the GraphQL-generated `Brand.brandThemeMode` enum
 * ("AUTO"|"LIGHT"|"DARK") into `LocalBrandConfig.BrandConfig`'s
 * lowercase `"auto"|"light"|"dark"` shape. Exported so callers that need
 * `brandThemeMode` outside of a full `Theme`/`getThemes()` computation
 * (e.g. `useActiveBrandTheme` in `ContentContainer`, which needs to
 * reactively re-resolve the active theme bucket independently of
 * generating the `Theme` objects themselves) don't need to duplicate this
 * mapping.
 */
export function resolveBrandThemeMode(
  brand: Graph.Maybe<Graph.Brand> | undefined,
): "auto" | "light" | "dark" | undefined {
  return brand?.brandThemeMode
    ? brandThemeModeMap[brand.brandThemeMode]
    : undefined;
}

function toBrandConfig(
  brand: Graph.Maybe<Graph.Brand> | undefined,
): LocalBrandConfig.BrandConfig | undefined {
  if (!brand) return undefined;

  const toPalette = (
    palette: Graph.Maybe<Graph.BrandPalette> | undefined,
  ): LocalBrandConfig.BrandPalette | undefined => {
    if (!palette) return undefined;
    return {
      primaryColor: palette.primaryColor ?? undefined,
      primaryActionColor: palette.primaryActionColor ?? undefined,
      accentColor: palette.accentColor ?? undefined,
      primaryDarkColor: palette.primaryDarkColor ?? undefined,
      primaryLightColor: palette.primaryLightColor ?? undefined,
      surfaceColor: palette.surfaceColor ?? undefined,
      textColor: palette.textColor ?? undefined,
      pageBackground: palette.pageBackground ?? undefined,
      logoUrl: palette.logoUrl ?? undefined,
      bannerUrl: palette.bannerUrl ?? undefined,
    };
  };

  return {
    light: toPalette(brand.light),
    dark: toPalette(brand.dark),
    brandThemeMode: resolveBrandThemeMode(brand),
    allowVisitorThemeOverride: brand.allowVisitorThemeOverride ?? undefined,
    brandFontUrl: brand.fontUrl ?? undefined,
    brandFontFamily: brand.fontFamily ?? undefined,
    logoUrl: brand.logoUrl ?? undefined,
    bannerUrl: brand.bannerUrl ?? undefined,
  };
}

/**
 * Converts legacy `CustomColours` into a synthetic, LIGHT-only
 * `BrandConfig` layer (legacy `customColours` predates the dark-theme
 * concept, so it can never customise a `dark` palette). `theme` maps onto
 * `brandThemeMode` so the legacy "force light/dark" behaviour is preserved.
 */
function legacyCustomColoursToBrandConfig(
  customColours: Graph.Maybe<Graph.CustomColours> | undefined,
): LocalBrandConfig.BrandConfig | undefined {
  if (!customColours) return undefined;
  return {
    light: {
      primaryColor: customColours.primary ?? undefined,
      primaryActionColor:
        customColours.primaryActionColour ?? customColours.primary ?? undefined,
      accentColor: customColours.accentColour ?? undefined,
      primaryDarkColor: customColours.primaryDarkColour ?? undefined,
      primaryLightColor: customColours.primaryLightColour ?? undefined,
      pageBackground: customColours.pageBackground ?? undefined,
      logoUrl: customColours.logoUrl ?? undefined,
    },
    brandThemeMode:
      customColours.theme === Graph.BrandTheme.Dark ? "dark" : "light",
    brandFontUrl: customColours.fontUrl ?? undefined,
    brandFontFamily: customColours.fontFamily ?? undefined,
  };
}

type GetThemeOptions = {
  customColours?: Graph.Maybe<Graph.CustomColours>;
  brand?: Graph.Maybe<Graph.Brand>;
  customFonts?: CustomFonts;
  /**
   * Canonical-schema brand surface/text colour overrides (mirroring
   * zod-types' `BrandPaletteSchema.surfaceColor`/`.textColor` fields). When
   * provided, these override the resolved palette's `surfaceColor`/
   * `textColor` for the theme being rendered, and every contrast-dependent
   * derived token (card/border/muted, textOnPrimary/textOnSecondary,
   * brandOnAction/brandOnTint, isDark.*Foreground) is re-derived against the
   * overridden value using the existing `pickAccessibleTextColour`/contrast
   * infrastructure below, rather than the resolved-palette values. When
   * absent, this uses whatever `resolveBrandPalette` resolves for
   * `surfaceColor`/`textColor` (falling back through `brand` ->
   * zod-types static defaults).
   */
  brandSurfaceColor?: Graph.Maybe<string>;
  brandTextColor?: Graph.Maybe<string>;
  /**
   * Which theme bucket ("light" | "dark") to generate a `Theme` for.
   * Defaults to "light" when omitted (matching the previous default
   * behaviour of `computeTheme`/`getTheme`). Use `getThemes()` below to
   * generate BOTH buckets side-by-side, plus the resolved "active" bucket
   * (accounting for `brand.brandThemeMode`, visitor override, and system
   * preference).
   */
  theme?: ResolvedThemeName;
};

/**
 * Contrast threshold for coloured foreground on a white background. If the
 * contrast ratio is lower than this number, we display dark foreground instead.
 */
const foregroundContrastThreshold = 4.5;
/**
 * Contrast threshold for white foreground on a coloured background. If the
 * contrast ratio is lower than this number, we display dark foreground instead.
 */
const backgroundContrastThreshold = 3;

/**
 * Non-customisable colours
 */
const white = "#FFFFFF";
const black = "#111";
const dark = "#1A1A1A";
const error = "#D0051C";
const success = "#218C60";
const info = "#175A94";
export const nonCustomisableColours = {
  black,
  dark,
  error,
  info,
  success,
  white,
};

/**
 * Default custom colours
 */
const defaultPrimary =
  LocalBrandConfig.APPOINTEDD_DEFAULT_LIGHT_PALETTE.primaryColor ?? "#583976";
const defaultSecondary =
  LocalBrandConfig.APPOINTEDD_DEFAULT_LIGHT_PALETTE.accentColor ?? "#3C5B58";
export const defaultCustomColours: Graph.CustomColours = {
  primary: defaultPrimary,
  secondary: defaultSecondary,
};

/**
 * Default fonts
 */
export const defaultRegularFont = "Lato";
export const defaultBoldFont = "Lato";
const defaultCustomFonts = {
  bold: defaultBoldFont,
  regular: defaultRegularFont,
};

/**
 * Per-theme surface/semantic tokens (not schema fields — derived design
 * tokens not carried by zod-types' `BrandPaletteSchema`, e.g.
 * success/error/info/card/border/muted). Dark-theme success/error/info are
 * brighter, more saturated variants of the light-theme values (same hue,
 * higher lightness), matching the approach used in micrositesv2's
 * BrandProvider.tsx THEME_TOKENS. `surface`/`text` mirror zod-types'
 * APPOINTEDD_DEFAULT_LIGHT_PALETTE/APPOINTEDD_DEFAULT_DARK_PALETTE
 * defaults, used only as the fallback when `resolveBrandPalette` doesn't
 * resolve a `surfaceColor`/`textColor` (which, given zod-types' own
 * defaults, should be rare/never in practice — kept here as a
 * belt-and-braces fallback and as the source for the non-schema semantic
 * tokens).
 */
const THEME_TOKENS = {
  dark: {
    border: "rgba(255,255,255,.12)",
    card: "#211d27",
    error: "#ff5064",
    info: "#3c99e9",
    muted: "rgba(243,238,230,.6)",
    success: "#4ade80",
    surface:
      LocalBrandConfig.APPOINTEDD_DEFAULT_DARK_PALETTE.surfaceColor ??
      "#17151b",
    text:
      LocalBrandConfig.APPOINTEDD_DEFAULT_DARK_PALETTE.textColor ?? "#f3eee6",
  },
  light: {
    border: "rgba(17,17,17,.1)",
    card: "#ffffff",
    error,
    info,
    muted: "rgba(17,17,17,.6)",
    success,
    surface:
      LocalBrandConfig.APPOINTEDD_DEFAULT_LIGHT_PALETTE.surfaceColor ??
      "#ffffff",
    text: LocalBrandConfig.APPOINTEDD_DEFAULT_LIGHT_PALETTE.textColor ?? dark,
  },
} as const;

/**
 * Ratio used to lighten a customised dark surface colour towards white when
 * deriving `--d-card` (the "raised card" tone). Dark UIs need a card
 * background that's visibly distinct from the page surface (matching the
 * existing static defaults, e.g. surface #17151b vs. card #211d27) rather
 * than an identical flat alias, which would remove all visual separation.
 * Kept in sync (same name/value) with micrositesV2's equivalent constant in
 * src/lib/brand-tokens.ts, pending eventual unification of the two repos'
 * brand-config schema packages.
 */
export const CARD_ELEVATION_TINT_RATIO = 0.05;

/**
 * Per-theme fallback for the "light tint" (`primaryLightColor`) design
 * token — not carried by zod-types' `BrandPaletteSchema` defaults, so kept
 * as a local derived-design-token fallback here, same as THEME_TOKENS
 * above. By design, this is a LIGHT tint in both the light AND dark theme
 * buckets, unlike surface/text which deliberately flip between buckets.
 */
const PRIMARY_LIGHT_TINT_DEFAULTS = {
  dark: "#c5d9f0",
  light: "#e7eef7",
} as const;

/**
 * Picks white or near-black text for best WCAG contrast against a given hex
 * background colour. Ported from micrositesv2's lib/brand.ts
 * pickAccessibleTextColor, reusing this codebase's existing getContrastWithWhite
 * luminance math (via parseRgb) rather than duplicating the relative-luminance
 * formula.
 *
 * Computes the actual contrast ratio of BOTH candidate text colours against
 * the background and picks whichever is higher (true "best contrast" logic),
 * rather than switching on a single fixed luminance threshold.
 */
function pickAccessibleTextColour(
  backgroundColour: string,
  {
    light = white,
    dark: darkColour = dark,
  }: { light?: string; dark?: string } = {},
): string {
  try {
    const contrastWithLight = getContrast(light, backgroundColour);
    const contrastWithDark = getContrast(darkColour, backgroundColour);
    return contrastWithDark > contrastWithLight ? darkColour : light;
  } catch {
    // Non-hex colours (e.g. a CSS variable reference, or an unexpected
    // value) — fall back to the light text colour rather than throwing.
    return light;
  }
}

// parseRgb is used indirectly via getContrastWithWhite (see that module).

/**
 * Generates a theme object containing style information for use throughout the
 * BookingTools, for a single, specific theme bucket ("light" | "dark").
 *
 * Uses `LocalBrandConfig.resolveBrandPalette` (from `@appointedd/zod-types`)
 * to resolve the effective brand palette for the requested theme, from (in
 * ascending precedence) a synthetic legacy-`customColours` layer and the
 * go-forward `brand` layer, falling back to Appointedd's static
 * per-theme default palettes (baked into `resolveBrandPalette` itself) for
 * any unset field. The richer semantic-token derivation (WCAG contrast,
 * success/error/info/card/border/muted, pickAccessibleTextColour) then
 * operates on top of that resolved palette.
 *
 * Wrapped in `memoize` (below) keyed on a stable serialization of `options`,
 * so repeated calls with an equivalent (but not necessarily
 * reference-identical) input return the exact same `Theme` object
 * reference, rather than a new object on every call.
 */
function computeTheme(
  options: GetThemeOptions = {
    customColours: defaultCustomColours,
    customFonts: defaultCustomFonts,
  },
): Theme {
  const customColours = options.customColours ?? defaultCustomColours;
  const customFonts = options.customFonts ?? defaultCustomFonts;
  const { primary: customPrimary, secondary: customSecondary } = customColours;

  const primary = customPrimary ?? defaultPrimary;
  const secondary = customSecondary ?? defaultSecondary;

  const themeBucket: ResolvedThemeName = options.theme ?? "light";
  const tokens = THEME_TOKENS[themeBucket];

  const layers = [
    legacyCustomColoursToBrandConfig(customColours),
    toBrandConfig(options.brand),
  ];
  const resolvedPalette = LocalBrandConfig.resolveBrandPalette(
    layers,
    themeBucket,
  );

  /**
   * Canonical-schema `brandSurfaceColor`/`brandTextColor` overrides (see
   * `GetThemeOptions` above). Each falls back independently to the
   * resolved palette's `surfaceColor`/`textColor` (itself already
   * defaulted by `resolveBrandPalette`) when unset, so passing only one of
   * the two doesn't perturb the other.
   */
  const hasSurfaceOverride =
    Boolean(options.brandSurfaceColor) ||
    layers.some((layer) => layer?.[themeBucket]?.surfaceColor !== undefined);
  const hasTextOverride = Boolean(options.brandTextColor);
  const effectiveSurface =
    options.brandSurfaceColor ?? resolvedPalette.surfaceColor ?? tokens.surface;
  const effectiveText = hasTextOverride
    ? (options.brandTextColor as string)
    : hasSurfaceOverride
    ? pickAccessibleTextColour(effectiveSurface)
    : resolvedPalette.textColor ?? tokens.text;
  /**
   * The "ink" pole used by `pickAccessibleTextColour`-based derivations
   * below (textOnPrimary/textOnSecondary/brandOnAction/brandOnTint). Only
   * swapped for `effectiveText` when `brandTextColor` is actually
   * overridden — otherwise this stays the static `dark` (#1A1A1A)
   * constant.
   */
  const inkPole = hasTextOverride ? effectiveText : dark;
  /**
   * `card`/`border`/`muted` are re-derived from `effectiveSurface` only
   * when `brandSurfaceColor` is actually overridden — otherwise they stay
   * the static per-bucket `tokens.card`/`.border`/`.muted`.
   */
  const isSurfaceDark =
    getContrastWithWhite(effectiveSurface) >= backgroundContrastThreshold;

  // border/muted are derived unconditionally from the resolved surface's
  // light/dark-ness. This is lossless when the surface hasn't been
  // customised: effectiveSurface === tokens.surface for that bucket in that
  // case, so isSurfaceDark matches the bucket itself and these resolve to
  // exactly the existing static tokens.border/tokens.muted values.
  const border = THEME_TOKENS[isSurfaceDark ? "dark" : "light"].border;
  const muted = THEME_TOKENS[isSurfaceDark ? "dark" : "light"].muted;

  // card stays the static per-bucket default when the org hasn't customised
  // their surface colour (byte-identical to today). When customised: for a
  // light surface, card matches the surface directly (mirrors the existing
  // light-bucket default, where card === surface). For a dark surface, card
  // is tinted lighter than the surface by CARD_ELEVATION_TINT_RATIO to
  // preserve the "raised card" visual separation dark UIs need — a flat
  // alias would make cards visually merge into the page background (see
  // CARD_ELEVATION_TINT_RATIO's doc comment above).
  const card = !hasSurfaceOverride
    ? tokens.card
    : isSurfaceDark
    ? applyOpacity(white, CARD_ELEVATION_TINT_RATIO, true, effectiveSurface)
    : effectiveSurface;

  const isDark = {
    primaryBackground:
      getContrastWithWhite(primary) >= backgroundContrastThreshold,
    primaryForeground:
      getContrast(primary, effectiveSurface) >= foregroundContrastThreshold,
    secondaryBackground:
      getContrastWithWhite(secondary) >= backgroundContrastThreshold,
    secondaryForeground:
      getContrast(secondary, effectiveSurface) >= foregroundContrastThreshold,
  };

  const textOnPrimary = isDark.primaryBackground ? white : inkPole;
  const primaryText = isDark.primaryForeground ? primary : effectiveText;

  const textOnSecondary = isDark.secondaryBackground ? white : inkPole;
  const secondaryText = isDark.secondaryForeground ? secondary : effectiveText;

  const brandPrimary =
    resolvedPalette.primaryActionColor ?? resolvedPalette.primaryColor;
  const brandPrimaryLight =
    resolvedPalette.primaryLightColor ??
    PRIMARY_LIGHT_TINT_DEFAULTS[themeBucket];
  const brandAccent = resolvedPalette.accentColor;
  const brandOnAction = pickAccessibleTextColour(
    brandPrimary as string,
    hasTextOverride ? { dark: inkPole } : undefined,
  );
  const brandOnTint = pickAccessibleTextColour(
    brandPrimaryLight,
    hasTextOverride ? { dark: inkPole } : undefined,
  );

  /* eslint-disable sort-keys */
  const cssVars: BrandCssVars = {
    "--d-surface": effectiveSurface,
    "--d-card": card,
    "--d-border": border,
    "--d-text": effectiveText,
    "--d-muted": muted,
    "--d-primary": brandPrimary as string,
    "--d-tint": brandPrimaryLight,
    "--d-on-tint": brandOnTint,
    "--d-accent": brandAccent as string,
    "--d-on-action": brandOnAction,
    "--d-success": tokens.success,
    "--d-error": tokens.error,
    "--d-info": tokens.info,
  };
  const brandFontFamily = options.brand?.fontFamily ?? undefined;
  if (brandFontFamily) {
    cssVars["--d-font-family"] = brandFontFamily;
  }
  /* eslint-enable sort-keys */

  /**
   * Resolves the fonts actually consumed by `typography.ts`'s shared text
   * styles (`theme.fonts.bold`/`.regular`), reconciling the two
   * independent font-configuration mechanisms:
   *
   * 1. The legacy per-widget uploaded font (`customFonts`, from
   *    `getCustomFonts()` — synthesized `@font-face` names "customBold"/
   *    "customRegular"), when actually set.
   * 2. Otherwise, the go-forward BrandConfig `fontFamily` (`brandFontFamily`
   *    above), so brand-configured fonts apply to body/heading/title/etc.
   *    text, not just the CSS `--d-font-family` var (which previously only
   *    reached `App.tsx`'s root font-family and Tailwind's
   *    `--font-b-heading`).
   * 3. Otherwise, the static "Lato" default (unchanged behaviour).
   */
  const resolvedFonts: CustomFonts = {
    bold:
      customFonts.bold !== defaultBoldFont
        ? customFonts.bold
        : brandFontFamily ?? defaultBoldFont,
    regular:
      customFonts.regular !== defaultRegularFont
        ? customFonts.regular
        : brandFontFamily ?? defaultRegularFont,
  };

  /* eslint-disable sort-keys */
  const colours = {
    primary,
    textOnPrimary,
    primaryText,

    secondary,
    textOnSecondary,
    secondaryText,

    isDark,

    black,
    dark: effectiveText,
    white: effectiveSurface,
    error: tokens.error,
    success: tokens.success,
    info: tokens.info,
  };
  /* eslint-enable sort-keys */

  const themePalette = options.brand?.[themeBucket];
  const resolvedLogoUrl =
    themePalette?.logoUrl ?? options.brand?.logoUrl ?? undefined;
  const resolvedPageBackground = resolvedPalette.pageBackground ?? undefined;

  return {
    colours,
    fonts: resolvedFonts,
    brand: {
      theme: themeBucket,
      logoUrl: resolvedLogoUrl,
      fontUrl: options.brand?.fontUrl ?? undefined,
      fontFamily: brandFontFamily,
      pageBackground: resolvedPageBackground,
      cssVars,
    },
  };
}

/**
 * Memoized `computeTheme`, keyed on a stable JSON serialization of the
 * `options` argument (rather than lodash's default reference-identity
 * resolver, which would never hit the cache given callers typically pass a
 * freshly-constructed `{ customColours, customFonts }` literal on every
 * render). Same external signature/return shape as `computeTheme`.
 *
 * Defaults to the "light" theme bucket when `options.theme` is omitted,
 * matching the previous default behaviour of this module. Use
 * `getThemes()` below to generate BOTH theme buckets side-by-side plus the
 * resolved "active" bucket.
 */
export const getTheme = memoize(computeTheme, (options?: GetThemeOptions) =>
  JSON.stringify(options ?? null),
);

/**
 * Generates BOTH the "light" and "dark" `Theme` objects side-by-side for
 * the given options, plus resolves which one is "active" by default via
 * `LocalBrandConfig.resolveActiveTheme` — accounting for
 * `brand.brandThemeMode`, whether visitors are allowed to override it
 * (`brand.allowVisitorThemeOverride`), an optional stored visitor
 * preference (`visitorOverride`, e.g. read from localStorage — see
 * BrandStyleTag.tsx), and the visitor's OS-level `prefers-color-scheme`
 * (`systemPreference`).
 *
 * This lets the app support live theme switching/preview (both Theme
 * objects are always available) without needing a full re-render pipeline
 * change — callers just swap which of `.light`/`.dark` they pass to
 * `ThemeProvider`/`BrandStyleTag`.
 */
export function getThemes(
  options: Omit<GetThemeOptions, "theme"> & {
    visitorOverride?: ResolvedThemeName | null;
    systemPreference?: ResolvedThemeName | null;
  } = {},
): { light: Theme; dark: Theme; active: ResolvedThemeName } {
  const { visitorOverride, systemPreference, ...rest } = options;

  const light = getTheme({ ...rest, theme: "light" });
  const dark = getTheme({ ...rest, theme: "dark" });

  const active = LocalBrandConfig.resolveActiveTheme({
    brandThemeMode: resolveBrandThemeMode(options.brand),
    allowVisitorThemeOverride:
      options.brand?.allowVisitorThemeOverride ?? undefined,
    visitorOverride,
    systemPreference,
  });

  return { active, dark, light };
}

/**
 * The default theme to be used if no custom colours or fonts are provided, or
 * in components which render before the Booking Tool config is loaded.
 * Sourced from `@appointedd/zod-types`'s `APPOINTEDD_DEFAULT_LIGHT_PALETTE`
 * (via `resolveBrandPalette`'s own fallback) as the single source of truth
 * for Appointedd's static default brand palette.
 */
export const defaultTheme: Theme = getTheme();

/**
 * A default LIGHT theme, i.e. the light-bucket equivalent of `defaultTheme`
 * — kept as a separate named export for existing consumers/tests/stories
 * that explicitly want the light bucket regardless of `defaultTheme`'s own
 * bucket.
 */
export const defaultLightTheme: Theme = getTheme({ theme: "light" });

/**
 * The default DARK theme, generated the same way as `defaultLightTheme`
 * but for the dark bucket — exposed for consumers/tests/stories that want
 * to preview/render the dark theme bucket directly.
 */
export const defaultDarkTheme: Theme = getTheme({ theme: "dark" });
